import { getAccessToken } from "../../utils/auth";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../utils/api";
import {
  Typography,
  Card,
  CardMedia,
  CardContent,
  Grid,
  Box,
  IconButton,
  Button,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CloseIcon from "@mui/icons-material/Close";

// Keyframes for loader animations
if (typeof document !== "undefined") {
  const styleId = "robotmsg-list3-loader-keyframes";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes loading-bar {
        0% { transform: translateX(-60%); }
        50% { transform: translateX(10%); }
        100% { transform: translateX(120%); }
      }
    `;
    document.head.appendChild(style);
  }
}

const styles = {
  pageWrap: {
    minHeight: "100vh",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    py: { xs: 2, md: 4 },
    px: { xs: 2, sm: 3, md: 4 },
    width: "100%",
    boxSizing: "border-box",
  },
  innerWrap: {
    width: "100%",
    maxWidth: 1200,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 2,
    mb: 3,
  },
  backButton: {
    bgcolor: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    "&:hover": {
      bgcolor: "#f5f5f5",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    },
  },
  title: {
    fontWeight: 700,
    color: "#1a1a2e",
    fontSize: { xs: "1.25rem", md: "1.5rem" },
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  subtitle: {
    color: "#64748b",
    fontSize: "0.875rem",
    mt: 0.5,
  },
  emptyState: {
    textAlign: "center",
    py: 8,
    px: 2,
    bgcolor: "white",
    borderRadius: 3,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.06)",
  },
  emptyIcon: {
    fontSize: 56,
    color: "#cbd5e1",
    mb: 2,
  },
  emptyTitle: {
    color: "#475569",
    fontWeight: 600,
    fontSize: "1.125rem",
  },
  emptySubtitle: {
    color: "#94a3b8",
    fontSize: "0.875rem",
    mt: 1,
  },
  errorCard: {
    textAlign: "center",
    py: 4,
    px: 3,
    bgcolor: "white",
    borderRadius: 3,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    border: "1px solid rgba(211, 47, 47, 0.2)",
  },
  imageCard: {
    borderRadius: 3,
    overflow: "hidden",
    bgcolor: "white",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.06)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
    },
  },
  imageMedia: {
    width: "100%",
    height: 240,
    objectFit: "cover",
    bgcolor: "#f1f5f9",
  },
  imageCaption: {
    py: 1.5,
    px: 2,
    borderTop: "1px solid rgba(0,0,0,0.06)",
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "#64748b",
  },
  imageClickHint: {
    mb: 2,
    borderRadius: 2,
    bgcolor: "rgba(13, 71, 161, 0.06)",
    color: "#334155",
    border: "1px solid rgba(13, 71, 161, 0.15)",
    "& .MuiAlert-icon": {
      color: "#0d47a1",
    },
  },
};

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.25;

const CameraImages = () => {
  const { messageId } = useParams();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const navigate = useNavigate();

  const openLightbox = (index) => {
    setSelectedImageIndex(index);
    setZoom(1);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const handleZoomIn = () => {
    setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP));
  };

  const handleZoomOut = () => {
    setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP));
  };

  const handleResetZoom = () => {
    setZoom(1);
  };

  const handleLightboxWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) handleZoomIn();
    else handleZoomOut();
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await axios.get(`/robotmsgs/message/${messageId}`, {
          headers: {
            Authorization: `Bearer ${getAccessToken()}`,
          },
        });
        setImages(response.data.data.camera_images || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching camera images:", err);
        setError("Failed to load the camera images. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [messageId]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
          px: 2,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 260,
            height: 260,
            borderRadius: "28px",
            bgcolor: "rgba(15,23,42,0.9)",
            boxShadow:
              "0 24px 60px rgba(15,23,42,0.55), 0 0 0 1px rgba(148,163,184,0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: "-40%",
              background:
                "conic-gradient(from 180deg at 50% 50%, rgba(56,189,248,0.2), rgba(129,140,248,0.35), rgba(56,189,248,0.2))",
              opacity: 0.8,
              filter: "blur(30px)",
              animation: "spin 12s linear infinite",
            }}
          />
          <Box
            sx={{
              position: "relative",
              width: 72,
              height: 72,
              borderRadius: "50%",
              bgcolor: "rgba(15,23,42,0.9)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 0 0 1px rgba(148,163,184,0.4), 0 0 0 12px rgba(15,23,42,0.9)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: -2,
                borderRadius: "50%",
                border: "3px solid transparent",
                borderTopColor: "#38bdf8",
                borderRightColor: "#6366f1",
                animation: "spin 1s linear infinite",
              }}
            />
            <Typography
              sx={{
                position: "relative",
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: "#e5e7eb",
              }}
            >
              Image            
              </Typography>
          </Box>
          <Box sx={{ position: "relative", textAlign: "center", px: 3 }}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 600,
                color: "#e5e7eb",
                mb: 0.5,
              }}
            >
              Loading camera images
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: "#9ca3af",
              }}
            >
              Fetching images for this message…
            </Typography>
          </Box>
          <Box
            sx={{
              position: "relative",
              width: "68%",
              height: 4,
              borderRadius: 999,
              bgcolor: "rgba(15,23,42,1)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(90deg, transparent, #38bdf8, #6366f1, transparent)",
                animation: "loading-bar 1.4s ease-in-out infinite",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.pageWrap}>
      <Box sx={styles.innerWrap}>
        <Box sx={styles.header}>
          <IconButton
            onClick={() => navigate(-1)}
            size="medium"
            sx={styles.backButton}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={styles.title}>Camera images</Typography>
            <Typography sx={styles.subtitle}>
              Message ID: {messageId} · View and inspect captured images
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!error && images.length === 0 && (
          <Box sx={styles.emptyState}>
            <PhotoCameraOutlinedIcon sx={styles.emptyIcon} />
            <Typography sx={styles.emptyTitle}>
              No camera images available
            </Typography>
            <Typography sx={styles.emptySubtitle}>
              There are no images captured for this message yet.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                mt: 2,
                textTransform: "none",
                borderRadius: 2,
                borderColor: "#0d47a1",
                color: "#0d47a1",
                "&:hover": {
                  borderColor: "#0a3d91",
                  bgcolor: "rgba(13, 71, 161, 0.08)",
                },
              }}
            >
              Back to messages
            </Button>
          </Box>
        )}

        {!error && images.length > 0 && (
          <>
            <Alert severity="info" icon={<ZoomInIcon />} sx={styles.imageClickHint}>
              <Typography component="span" sx={{ fontWeight: 600, display: "block", mb: 0.25 }}>
                Click an image to view it larger
              </Typography>
              <Typography component="span" variant="body2" sx={{ color: "#64748b", lineHeight: 1.5 }}>
                Opens a full-screen viewer. Use the zoom buttons or scroll (mouse wheel) on the
                image to zoom in or out.
              </Typography>
            </Alert>
          <Grid container spacing={3}>
            {images.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} lg={4} key={index}>
                <Card
                  elevation={0}
                  sx={styles.imageCard}
                  onClick={() => openLightbox(index)}
                >
                  <CardMedia
                    component="img"
                    image={`data:image/jpeg;base64,${image}`}
                    alt={`Camera image ${index + 1}`}
                    sx={styles.imageMedia}
                    onError={(e) => {
                      e.target.style.background = "#fef2f2";
                      e.target.alt = "Image failed to load";
                    }}
                  />
                  <CardContent sx={styles.imageCaption}>
                    Image {index + 1} of {images.length}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          </>
        )}

        {/* Image lightbox with zoom */}
        <Dialog
          open={lightboxOpen}
          onClose={closeLightbox}
          maxWidth={false}
          fullWidth
          PaperProps={{
            sx: {
              maxWidth: "95vw",
              maxHeight: "95vh",
              bgcolor: "rgba(15,23,42,0.97)",
              borderRadius: 3,
              overflow: "hidden",
            },
          }}
          BackdropProps={{ sx: { bgcolor: "rgba(0,0,0,0.85)" } }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.5,
              px: 2,
              borderBottom: "1px solid rgba(255,255,255,0.1)",
              bgcolor: "rgba(15,23,42,0.98)",
            }}
          >
            <Typography variant="subtitle1" sx={{ color: "#e2e8f0", fontWeight: 600 }}>
              Image {selectedImageIndex + 1} of {images.length}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                onClick={handleZoomOut}
                disabled={zoom <= MIN_ZOOM}
                sx={{
                  color: "#94a3b8",
                  "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                  "&.Mui-disabled": { color: "#475569" },
                }}
                size="small"
              >
                <ZoomOutIcon />
              </IconButton>
              <Button
                size="small"
                onClick={handleResetZoom}
                sx={{
                  minWidth: 64,
                  color: "#94a3b8",
                  fontSize: "0.75rem",
                  textTransform: "none",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.08)", color: "#fff" },
                }}
              >
                {Math.round(zoom * 100)}%
              </Button>
              <IconButton
                onClick={handleZoomIn}
                disabled={zoom >= MAX_ZOOM}
                sx={{
                  color: "#94a3b8",
                  "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                  "&.Mui-disabled": { color: "#475569" },
                }}
                size="small"
              >
                <ZoomInIcon />
              </IconButton>
              <IconButton
                onClick={closeLightbox}
                sx={{
                  color: "#94a3b8",
                  ml: 1,
                  "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.08)" },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{
              p: 0,
              overflow: "auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 400,
              bgcolor: "rgba(15,23,42,0.95)",
            }}
            onWheel={handleLightboxWheel}
          >
            {images[selectedImageIndex] && (
              <Box
                sx={{
                  width: `${80 * zoom}%`,
                  minWidth: "min(80vw, 80%)",
                  height: `${70 * zoom}%`,
                  minHeight: "min(70vh, 70%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "width 0.2s ease, height 0.2s ease",
                }}
              >
                <Box
                  component="img"
                  src={`data:image/jpeg;base64,${images[selectedImageIndex]}`}
                  alt={`Camera image ${selectedImageIndex + 1}`}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                  draggable={false}
                  onClick={(e) => e.stopPropagation()}
                />
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};

export default CameraImages;
